
import {Component, Vue} from 'vue-property-decorator';
import CookieDlg from "@/components/CookieDlg.vue";
import Header from "@/components/Header.vue";
import {
  CheckoutEventNames,
  CheckoutEventsData, CheckoutLineItem,
  CheckoutOpenLineItem,
  Environments,
  initializePaddle,
  Paddle,
  PaddleEventData
} from "@paddle/paddle-js";
import Cookies from "js-cookie";
import $ from "jquery";
import CheckoutSuccess from "@/views/CheckoutSuccess.vue";
import {
  gaSendCheckoutCompleted,
  gaSendCheckoutItem,
  gaSendCheckoutStarted,
  gaSendCheckoutView,
  gaSendExperiment,
  gaSendPageview, trackEvent
} from "@/components/Analytics";
import {
  EXPERIMENT_CHECKOUT_PRESETS,
  EXPERIMENT_CHECKOUT_PRESETS_ON
} from "@/views/Experiments";
import {installGanalytics} from "@/components/Tracker";

interface CheckoutItem {
  priceId: string;
  platform: string;
}
@Component({
  components: {CheckoutSuccess, Header, CookieDlg}
})
export default class Checkout extends Vue {
  private PADDLE_PROD_TOKEN = "live_e02315a49814cd83dfd9bde3251";
  private PADDLE_PROD_ENVIRONMENT: Environments = "production";
  private PADDLE_TEST_ENVIRONMENT: Environments = "sandbox";
  private PADDLE_TEST_TOKEN = "test_2599d10df3f92d7142eedbbae18";
  private TEST_ITEMS = {
    win: {
      priceId: "pri_01jck9t2zbr8w11gd7avybpmvr",
      platform: "win"
    },
    mac: {
      priceId: "pri_01jckbr26f8sq34m7v3m0hqnsy",
      platform: "mac"
    },
    lin: {
      priceId: "pri_01jcncc17ht48qyz5pv1dte3tj",
      platform: "lin"
    },
    zip: {
      priceId: "pri_01jcnccs2e22gsnyvf8cj1v348",
      platform: "zip"
    }
  }
  private PROD_ITEMS = {
    win: {
      priceId: "pri_01jcnjky3v7x7ats32zaat8rxs",
      platform: "win"
    },
    mac: {
      priceId: "pri_01jcnjpn6yyrd9nfbp25sv0a9t",
      platform: "mac"
    },
    lin: {
      priceId: "pri_01jcnjqj85phb7t80sa7zty3r5",
      platform: "lin"
    },
    zip: {
      priceId: "pri_01jcnjr4eze6h73gjp32xegbbj",
      platform: "zip"
    }
  };
  private experimentCase = 0;
  private cdnUrl(path: string): string {
    return path;
  }

  private PADDLE_ENVIRONMENT = this.PADDLE_PROD_ENVIRONMENT;
  private PADDLE_TOKEN = this.PADDLE_ENVIRONMENT === 'production' ? this.PADDLE_PROD_TOKEN : this.PADDLE_TEST_TOKEN;
  private ITEMS = this.PADDLE_ENVIRONMENT === 'production' ? this.PROD_ITEMS : this.TEST_ITEMS;
  private ITEMS_LIST = [this.ITEMS.win, this.ITEMS.mac, this.ITEMS.lin, this.ITEMS.zip];
  private CHECKOUT_START = 1;
  private CHECKOUT_SUCCESS = 2;
  private needsCookieConsent = Cookies.get("CookieConsent") !== "true";
  public $refs!: {
    cookieDlg: CookieDlg;
  };

  private amount: number = null;

  private platform: CheckoutItem = this.ITEMS.win;
  private paddleItem: CheckoutOpenLineItem = this.ITEMS.win;
  private totalSum = '';
  private totalTaxes?: string = '';
  private currency = 'USD';
  private paddle: Paddle | undefined;
  private isCheckoutReady = false;
  private validationError?: string = null;
  private showSumRangeTip = false;
  private state = this.CHECKOUT_START;
  private transactionId = '';
  private platformId = '';
  private stagingCheckoutUpdates: CheckoutLineItem[] = [];
  // Rapid amount changes (e.g. when the user holds an arrow key) are coalesced into a single
  // updateItems request sent after this period of inactivity, in milliseconds.
  private CHECKOUT_UPDATE_DELAY_MS = 400;
  private checkoutUpdateTimerId = 0;
  private get isPresetsExperiment(): boolean {
    return this.experimentCase === EXPERIMENT_CHECKOUT_PRESETS_ON;
  }
  private selectedPreset: number | "other" = 5;
  private PRESETS = [5, 10];
  // Purchases of this amount or more come with a GanttProject Cloud promo code worth 5 credits.
  private PROMO_MIN_AMOUNT = 10;

  private isCheckoutRevealed = false;

  // True when the emotion image with "Continue to Checkout" button covers the Paddle form.
  private get isTeaserVisible(): boolean {
    return this.isPresetsExperiment && !this.isCheckoutRevealed;
  }

  private get isAmountValid(): boolean {
    return this.amount >= 5;
  }

  private get isCheckoutVisible(): boolean {
    return !this.isTeaserVisible;
  }

  private get isPromoCodePromised(): boolean {
    return this.amount >= this.PROMO_MIN_AMOUNT;
  }

  public async mounted() {
    if (this.needsCookieConsent) {
      this.$refs.cookieDlg.show();
    }
    gaSendPageview();
    installGanalytics();

    const fragment = window.location.hash;
    const platformName = fragment.startsWith("#") ? fragment.substring(1) : fragment;
    const platformArg = this.ITEMS_LIST.find(it => it.platform === platformName);
    if (platformArg) {
      this.platform = platformArg;
    }
    gaSendCheckoutView(this.platform.platform);
    trackEvent("pay-checkout-open");

    this.experimentCase = this.getExperimentOverride();
    console.log("Experiment case: " + this.experimentCase);

    if (this.experimentCase == -1) {
      try {
        const response = await fetch('/experiments');
        const data = await response.json();
        this.experimentCase = data[EXPERIMENT_CHECKOUT_PRESETS] || 0;
      } catch (e) {
        console.error("Failed to fetch experiments", e);
      }
    }

    gaSendExperiment(EXPERIMENT_CHECKOUT_PRESETS, this.experimentCase);
    if (this.experimentCase === EXPERIMENT_CHECKOUT_PRESETS_ON) {
      trackEvent("pay-presets-on");
    } else {
      trackEvent("pay-presets-off");
    }

    try {
      this.paddle = await initializePaddle({
        environment: this.PADDLE_ENVIRONMENT,
        token: this.PADDLE_TOKEN,
        checkout: {
          settings: {
            displayMode: "inline",
            frameTarget: "checkout-page",
            frameInitialHeight: 450,
            frameStyle: "width: 100%; min-width: 312px; background-color: transparent; border: none;"
          }
        },
        pwCustomer: {},
        eventCallback: this.onPaddleEvent
      });
      this.openCheckout();
      this.focusAmount();
      const intervalId = window.setInterval(function() {
        if (document.activeElement != document.getElementById("amount")) {
          $("#amount").trigger("focus");
          window.clearInterval(intervalId);
        }
      }, 100);
      console.log("paddle initialized");
    } catch (e) {
      console.error("Failed to initialize Paddle", e);
    }

    if (this.experimentCase == EXPERIMENT_CHECKOUT_PRESETS_ON) {
      this.selectPreset(5);
    } else {
      this.amount = 5;
      this.updateCheckout();
    }
  }

  public destroyed() {
    window.clearTimeout(this.checkoutUpdateTimerId);
  }

  private focusAmount() {
    if (this.isPresetsExperiment && this.selectedPreset !== "other") {
      return;
    }
    $("#amount").trigger("focus");
  }

  private getExperimentOverride(): number {
    return new URLSearchParams(window.location.search).get(EXPERIMENT_CHECKOUT_PRESETS) === "1"
        ? EXPERIMENT_CHECKOUT_PRESETS_ON : -1;
  }

  private selectPreset(preset: number | "other") {
    this.selectedPreset = preset;
    // Every preset shows its emotion image first; the Paddle form is revealed by the button below it.
    this.isCheckoutRevealed = false;
    if (preset !== "other") {
      this.amount = preset;
    }
    this.updateCheckout();
    if (preset === "other") {
      this.$nextTick(() => this.focusAmount());
    }
  }

  private continueToCheckout() {
    if (!this.isAmountValid) {
      return;
    }
    this.isCheckoutRevealed = true;
    trackEvent("pay-continue-checkout", {"amount": this.amount});
  }

  private openCheckout() {
    this.paddleItem.quantity = this.amount || 1;
    this.paddle?.Checkout.open({
      items: [
        {priceId: this.platform.priceId, quantity: this.amount}
      ]
    });
    this.validationError = (this.amount >= 5) ? null : "Please enter your price";
    gaSendCheckoutView(this.platform.platform);
  }

  private updateCheckout() {
    if (this.amount < 5) {
      this.validationError = "Please enter any sum starting from 5 USD";
      this.showSumRangeTip = true;
      return;
    } else {
      this.validationError = null;
      this.showSumRangeTip = false;
    }
    this.stagingCheckoutUpdates = [
      {priceId: this.platform.priceId, quantity: this.amount}
    ];
    if (this.isCheckoutReady) {
      this.scheduleCheckoutUpdates();
    }
  }

  private scheduleCheckoutUpdates() {
    window.clearTimeout(this.checkoutUpdateTimerId);
    this.checkoutUpdateTimerId = window.setTimeout(() => this.sendCheckoutUpdates(), this.CHECKOUT_UPDATE_DELAY_MS);
  }

  private sendCheckoutUpdates() {
    window.clearTimeout(this.checkoutUpdateTimerId);
    this.checkoutUpdateTimerId = 0;
    if (this.stagingCheckoutUpdates?.length > 0) {
      this.paddle?.Checkout.updateItems(this.stagingCheckoutUpdates);
      this.stagingCheckoutUpdates = [];
    }
  }

  private onPaddleEvent(event: PaddleEventData) {
    if (!event.name) {
      return;
    }
    switch (event.name) {
      case CheckoutEventNames.CHECKOUT_COMPLETED: {
        this.onCheckoutCompleted(event.data);
        break;
      }
      case CheckoutEventNames.CHECKOUT_CUSTOMER_CREATED: {
        gaSendCheckoutStarted(this.platform.platform, event.data.totals.subtotal);
        break;
      }
      case CheckoutEventNames.CHECKOUT_ITEMS_UPDATED: {
        this.totalSum = event.data.totals.total.toFixed(2);
        this.totalTaxes = event.data.totals.tax.toFixed(2);
        this.currency = event.data.currency_code
        //gaSendCheckoutItem(this.platform.platform, this.amount);
        break;
      }
      case CheckoutEventNames.CHECKOUT_LOADED: {
        this.isCheckoutReady = true;
        this.sendCheckoutUpdates();
      }
    }
  }

  private onCheckoutCompleted(data: CheckoutEventsData) {
    this.state = this.CHECKOUT_SUCCESS;
    this.transactionId = data.transaction_id;
    this.platformId = this.ITEMS_LIST.find(it => it.priceId === data.items[0].price_id).platform;
    gaSendCheckoutCompleted(this.platform.platform, data.totals.subtotal, this.transactionId, this.experimentCase);
    trackEvent("pay-success", {
      "amount": this.amount,
      "pay-presets-case": this.experimentCase,
      "customer": data.customer.email,
      "txn": data.transaction_id
    })
  }

  private whenEnabled(classes: string): string {
    return this.validationError ? "disabled " + classes : classes;
  }
}
